import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ConditionEditor from "./ConditionEditor";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaPlay,
  FaPause,
  FaArchive,
  FaChartBar,
  FaUsers,
  FaComments,
  FaEye,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaNetworkWired,
} from "react-icons/fa";

const ConditionManager = () => {
  const navigate = useNavigate();
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isNewCondition, setIsNewCondition] = useState(false);
  const [conditionStats, setConditionStats] = useState({});
  const [conditionStudyMap, setConditionStudyMap] = useState({});
  const [showInstances, setShowInstances] = useState(null);
  const [instances, setInstances] = useState([]);

  // Read admin token directly from localStorage (separate from user auth)
  const getAdminToken = () => localStorage.getItem("admin_token");

  useEffect(() => {
    fetchConditions();
  }, []);

  const fetchConditions = async () => {
    setLoading(true);
    const token = getAdminToken();
    try {
      const { data } = await api.get("/admin/conditions", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConditions(data);

      // Fetch stats and study map in parallel
      const statsPromises = data.map((c) =>
        api
          .get(`/admin/conditions/${c._id}/stats`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => ({ id: c._id, stats: res.data }))
          .catch(() => ({ id: c._id, stats: null }))
      );
      const studyMapPromise = api
        .get("/admin/condition-study-map", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => res.data)
        .catch(() => ({}));

      const [statsResults, studyMap] = await Promise.all([
        Promise.all(statsPromises),
        studyMapPromise,
      ]);
      const statsMap = {};
      statsResults.forEach((r) => {
        statsMap[r.id] = r.stats;
      });
      setConditionStats(statsMap);
      setConditionStudyMap(studyMap);
    } catch (err) {
      console.error("Error fetching conditions:", err);
      setError("Failed to fetch conditions");
    } finally {
      setLoading(false);
    }
  };

  const fetchInstances = async (conditionId) => {
    const token = getAdminToken();
    try {
      const { data } = await api.get(`/admin/conditions/${conditionId}/instances`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInstances(data);
      setShowInstances(conditionId);
    } catch (err) {
      console.error("Error fetching instances:", err);
      setError("Failed to fetch instances");
    }
  };

  const handleCreateNew = () => {
    setSelectedCondition(null);
    setIsNewCondition(true);
    setShowEditor(true);
  };

  const handleEdit = (condition) => {
    setSelectedCondition(condition);
    setIsNewCondition(false);
    setShowEditor(true);
  };

  const handleSave = async (formData) => {
    const token = getAdminToken();
    try {
      if (isNewCondition) {
        await api.post(
          "/admin/conditions",
          {
            id: formData.id,
            name: formData.name,
            description: formData.description,
            topic: formData.topic,
            agents: formData.agents,
            timing: formData.timing,
            experimentalFactors: formData.experimentalFactors,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await api.put(
          `/admin/conditions/${formData.id}`,
          {
            name: formData.name,
            description: formData.description,
            topic: formData.topic,
            agents: formData.agents,
            timing: formData.timing,
            experimentalFactors: formData.experimentalFactors,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setShowEditor(false);
      fetchConditions();
    } catch (err) {
      console.error("Error saving condition:", err);
      setError(err.response?.data?.message || "Failed to save condition");
    }
  };

  const handleStatusChange = async (conditionId, newStatus) => {
    const token = getAdminToken();
    try {
      await api.patch(
        `/admin/conditions/${conditionId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchConditions();
    } catch (err) {
      console.error("Error updating status:", err);
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDuplicate = async (condition) => {
    const newId = prompt("Enter ID for the duplicate:", `${condition._id}_copy`);
    if (!newId) return;
    const newName = prompt("Enter name for the duplicate:", `${condition.name} (Copy)`);
    if (!newName) return;

    const token = getAdminToken();
    try {
      await api.post(
        `/admin/conditions/${condition._id}/duplicate`,
        { newId, newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchConditions();
    } catch (err) {
      console.error("Error duplicating condition:", err);
      setError(err.response?.data?.message || "Failed to duplicate condition");
    }
  };

  const handleDelete = async (condition) => {
    const confirmText = `DELETE ${condition._id}`;
    const userInput = prompt(
      `This will permanently delete condition "${condition.name}" and ALL associated data.\n\nType "${confirmText}" to confirm:`
    );
    if (userInput !== confirmText) {
      alert("Deletion cancelled");
      return;
    }

    const token = getAdminToken();
    try {
      await api.delete(`/admin/conditions/${condition._id}?force=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchConditions();
    } catch (err) {
      console.error("Error deleting condition:", err);
      setError(err.response?.data?.message || "Failed to delete condition");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-500";
      case "ready":
        return "bg-yellow-500";
      case "active":
        return "bg-green-500";
      case "archived":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusActions = (condition) => {
    switch (condition.status) {
      case "draft":
        return (
          <>
            <button
              onClick={() => handleStatusChange(condition._id, "ready")}
              className="text-yellow-400 hover:text-yellow-300 p-1"
              title="Mark Ready"
            >
              <FaCheck />
            </button>
          </>
        );
      case "ready":
        return (
          <>
            <button
              onClick={() => handleStatusChange(condition._id, "draft")}
              className="text-gray-400 hover:text-gray-300 p-1"
              title="Back to Draft"
            >
              <FaTimes />
            </button>
            <button
              onClick={() => handleStatusChange(condition._id, "active")}
              className="text-green-400 hover:text-green-300 p-1"
              title="Activate"
            >
              <FaPlay />
            </button>
          </>
        );
      case "active":
        return (
          <button
            onClick={() => handleStatusChange(condition._id, "archived")}
            className="text-red-400 hover:text-red-300 p-1"
            title="Archive"
          >
            <FaArchive />
          </button>
        );
      case "archived":
        return (
          <button
            onClick={() => handleStatusChange(condition._id, "draft")}
            className="text-gray-400 hover:text-gray-300 p-1"
            title="Restore to Draft"
          >
            <FaPlay />
          </button>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-blue-400 text-3xl" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900 p-6 rounded-lg">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Condition Management</h2>
          <p className="text-gray-400">Create and manage experimental conditions</p>
        </div>
        <button
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
        >
          <FaPlus className="mr-2" /> New Condition
        </button>
      </div>

      {error && (
        <div className="bg-red-600 text-white p-3 rounded mb-4 flex justify-between items-center">
          {error}
          <button onClick={() => setError("")} className="text-white hover:text-gray-200">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Conditions List */}
      <div className="space-y-4">
        {conditions.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-lg">
            <p className="text-gray-400 mb-4">No conditions created yet</p>
            <button
              onClick={handleCreateNew}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Create Your First Condition
            </button>
          </div>
        ) : (
          conditions.map((condition) => (
            <div key={condition._id} className="bg-gray-800 rounded-lg overflow-hidden">
              {/* Condition Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold text-white ${getStatusColor(
                      condition.status
                    )}`}
                  >
                    {condition.status.toUpperCase()}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-white">{condition.name}</h3>
                      {conditionStudyMap[condition._id] ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-600 text-indigo-100">
                          {conditionStudyMap[condition._id]}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-600 text-gray-300">
                          Standalone
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm">
                      ID: {condition._id} | Topic: {condition.topic?.name || "Not set"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Stats Summary */}
                  {conditionStats[condition._id] && (
                    <div className="flex items-center space-x-4 mr-4 text-sm">
                      <span className="text-gray-400 flex items-center">
                        <FaUsers className="mr-1" />
                        {conditionStats[condition._id].instances?.total || 0} instances
                      </span>
                      <span className="text-gray-400 flex items-center">
                        <FaComments className="mr-1" />
                        {conditionStats[condition._id].content?.messages || 0} posts
                      </span>
                    </div>
                  )}

                  {/* Status Actions */}
                  <div className="flex items-center border-r border-gray-600 pr-2 mr-2">
                    {getStatusActions(condition)}
                  </div>

                  {/* General Actions */}
                  <button
                    onClick={() => navigate(`/admin/conditions/${condition._id}/analytics`)}
                    className="text-green-400 hover:text-green-300 p-2"
                    title="View Analytics"
                  >
                    <FaChartBar />
                  </button>
                  <button
                    onClick={() => fetchInstances(condition._id)}
                    className="text-blue-400 hover:text-blue-300 p-2"
                    title="View Instances"
                  >
                    <FaEye />
                  </button>
                  <button
                    onClick={() => handleEdit(condition)}
                    className="text-blue-400 hover:text-blue-300 p-2"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDuplicate(condition)}
                    className="text-purple-400 hover:text-purple-300 p-2"
                    title="Duplicate"
                  >
                    <FaCopy />
                  </button>
                  {!conditionStudyMap[condition._id] && (
                    <button
                      onClick={() => handleDelete(condition)}
                      className="text-red-400 hover:text-red-300 p-2"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>

              {/* Condition Details */}
              <div className="px-4 pb-4 grid grid-cols-4 gap-4 text-sm">
                <div className="bg-gray-700 p-3 rounded">
                  <p className="text-gray-400">Agents</p>
                  <p className="text-white font-semibold">{condition.agents?.count || 12}</p>
                </div>
                <div className="bg-gray-700 p-3 rounded">
                  <p className="text-gray-400">Style / Focus</p>
                  <p className="text-white font-semibold capitalize">
                    {condition.agents?.communicationStyle || "emotional"} / {condition.agents?.groupFocus || "neutral"}
                  </p>
                </div>
                <div className="bg-gray-700 p-3 rounded">
                  <p className="text-gray-400">Stance Split</p>
                  <p className="text-white font-semibold">
                    {Math.round((condition.agents?.stanceDistribution?.pro || 0.5) * 100)}% pro / {Math.round((condition.agents?.stanceDistribution?.contra || 0.5) * 100)}% contra
                  </p>
                </div>
                <div className="bg-gray-700 p-3 rounded">
                  <p className="text-gray-400">Pre-population</p>
                  <p className="text-white font-semibold">
                    {condition.timing?.prePopulationPosts || 20} posts
                  </p>
                </div>
              </div>

              {/* Instances Panel */}
              {showInstances === condition._id && (
                <div className="border-t border-gray-700 p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-white font-semibold">Instances</h4>
                    <button
                      onClick={() => setShowInstances(null)}
                      className="text-gray-400 hover:text-white"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  {instances.length === 0 ? (
                    <p className="text-gray-400">No instances yet</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {instances.map((instance) => (
                        <div
                          key={instance._id}
                          className="bg-gray-700 p-3 rounded flex justify-between items-center"
                        >
                          <div>
                            <p className="text-white text-sm">
                              {instance.participantId}
                              {instance.isTestInstance && (
                                <span className="ml-2 text-xs bg-yellow-600 px-1 rounded">TEST</span>
                              )}
                            </p>
                            <p className="text-gray-400 text-xs">
                              Created: {new Date(instance.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                instance.status === "active"
                                  ? "bg-green-600"
                                  : instance.status === "completed"
                                  ? "bg-blue-600"
                                  : instance.status === "error"
                                  ? "bg-red-600"
                                  : "bg-gray-600"
                              } text-white`}
                            >
                              {instance.status}
                            </span>
                            {instance.populationProgress && (
                              <span className="text-gray-400 text-xs">
                                {instance.getProgressPercentage
                                  ? `${instance.getProgressPercentage()}%`
                                  : `${instance.populationProgress.agentsCreated}/${instance.populationProgress.agentsTotal} agents`}
                              </span>
                            )}
                            <button
                              onClick={() => navigate(`/admin/instances/${instance._id}`)}
                              className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center"
                              title="View Analytics"
                            >
                              <FaNetworkWired className="mr-1" /> Analytics
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl">
            <ConditionEditor
              condition={selectedCondition}
              onSave={handleSave}
              onCancel={() => setShowEditor(false)}
              isNew={isNewCondition}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionManager;
